import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { AircraftMakesService } from '../../services/intellijetnet/aircraft-makes.service'
import { AircraftModelsService } from '../../services/intellijetnet/aircraft-models.service'
import { AircraftMake, AircraftModel } from '../../types/intellijetnet'
import { capitalizeMakeName, formatModelName } from '../../utils/formatters'

export default function MakeModelSelector() {
  const navigate = useNavigate()
  const [makes, setMakes] = useState<AircraftMake[]>([])
  const [models, setModels] = useState<AircraftModel[]>([])
  const [selectedMake, setSelectedMake] = useState<number | null>(null)
  const [selectedModel, setSelectedModel] = useState<number | null>(null)
  const [loadingMakes, setLoadingMakes] = useState(true)
  const [loadingModels, setLoadingModels] = useState(false)

  useEffect(() => {
    fetchMakes()
  }, [])

  useEffect(() => {
    if (selectedMake) {
      fetchModels(selectedMake)
    } else {
      setModels([])
      setSelectedModel(null)
    }
  }, [selectedMake])

  const fetchMakes = async () => {
    try {
      setLoadingMakes(true)
      const data = await AircraftMakesService.getAll()
      setMakes(data)
    } catch (error) {
      // Error fetching makes
    } finally {
      setLoadingMakes(false)
    }
  }

  const fetchModels = async (makeId: number) => {
    try {
      setLoadingModels(true)
      const data = await AircraftModelsService.getByMakeId(makeId)
      setModels(data)
    } catch (error) {
      // Error fetching models
    } finally {
      setLoadingModels(false)
    }
  }

  const handleGoToMarket = () => {
    if (selectedModel) {
      navigate(`/market/${selectedModel}`)
    }
  }

  return (
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        Quick Market Access
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Make Selector */}
        <div>
          <label htmlFor="make-select" className="block text-sm font-medium text-gray-700 mb-2">
            Select Make
          </label>
          <select
            id="make-select"
            value={selectedMake || ''}
            onChange={(e) => setSelectedMake(e.target.value ? parseInt(e.target.value, 10) : null)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
            disabled={loadingMakes}
          >
            <option value="">
              {loadingMakes ? 'Loading makes...' : 'Choose a make'}
            </option>
            {makes.map((make) => (
              <option key={make.make_id} value={make.make_id}>
                {capitalizeMakeName(make.make_name)}
              </option>
            ))}
          </select>
        </div>

        {/* Model Selector */}
        <div>
          <label htmlFor="model-select" className="block text-sm font-medium text-gray-700 mb-2">
            Select Model
          </label>
          <select
            id="model-select"
            value={selectedModel || ''}
            onChange={(e) => setSelectedModel(e.target.value ? parseInt(e.target.value, 10) : null)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
            disabled={!selectedMake || loadingModels}
          >
            <option value="">
              {!selectedMake 
                ? 'Select a make first' 
                : loadingModels 
                ? 'Loading models...' 
                : 'Choose a model'}
            </option>
            {models.map((model) => (
              <option key={model.model_id} value={model.model_id}>
                {formatModelName(model.model)}
              </option>
            ))}
          </select>
        </div>

        {/* Go Button */}
        <div className="flex items-end">
          <button
            onClick={handleGoToMarket}
            disabled={!selectedModel}
            className="w-full px-4 py-2 bg-purple-950 text-white rounded-md hover:bg-purple-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
          >
            Go to Market
          </button>
        </div>
      </div>
    </div>
  )
}